import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

// مكون لتحسين إمكانية الوصول
const AccessibilityEnhancer = ({ children, announceChanges = true }) => {
  const announcementRef = useRef(null);

  useEffect(() => {
    // إضافة منطقة الإعلانات للقارئات الشاشة
    if (announceChanges && !document.getElementById('accessibility-announcements')) {
      const announcer = document.createElement('div');
      announcer.id = 'accessibility-announcements';
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      announcer.style.position = 'absolute';
      announcer.style.left = '-10000px';
      announcer.style.width = '1px';
      announcer.style.height = '1px';
      announcer.style.overflow = 'hidden';
      document.body.appendChild(announcer);
    }

    // تحسين التنقل بلوحة المفاتيح
    const handleKeyDown = (event) => {
      // تحسين التنقل بـ Tab
      if (event.key === 'Tab') {
        document.body.classList.add('keyboard-navigation');
      }
      
      // إضافة اختصارات لوحة المفاتيح
      if (event.altKey) {
        switch (event.key) {
          case 'm':
            // الانتقال إلى القائمة الرئيسية
            event.preventDefault();
            const mainNav = document.querySelector('[role="navigation"]') || 
                           document.querySelector('nav') ||
                           document.querySelector('.sidebar');
            if (mainNav) {
              const firstLink = mainNav.querySelector('a, button');
              if (firstLink) firstLink.focus();
            }
            break;
          case 'c':
            // الانتقال إلى المحتوى الرئيسي
            event.preventDefault();
            const mainContent = document.querySelector('[role="main"]') || 
                               document.querySelector('main') ||
                               document.querySelector('.main-content');
            if (mainContent) {
              mainContent.focus();
              mainContent.scrollIntoView({ behavior: 'smooth' });
            }
            break;
          case 's':
            // الانتقال إلى البحث
            event.preventDefault();
            const searchInput = document.querySelector('input[type="search"]') ||
                               document.querySelector('input[placeholder*="بحث"]') ||
                               document.querySelector('input[placeholder*="search"]');
            if (searchInput) searchInput.focus();
            break;
        }
      }
    };

    const handleMouseDown = () => {
      document.body.classList.remove('keyboard-navigation');
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [announceChanges]);

  return children;
};

// مكون لتحسين التركيز
export const FocusManager = ({ children, trapFocus = false, restoreFocus = true }) => {
  const containerRef = useRef(null);
  const previousActiveElement = useRef(null);

  useEffect(() => {
    if (restoreFocus) {
      previousActiveElement.current = document.activeElement;
    }

    if (trapFocus && containerRef.current) {
      const focusableElements = containerRef.current.querySelectorAll(
        'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
      );
      
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleTabKey = (e) => {
        if (e.key === 'Tab') {
          if (e.shiftKey) {
            if (document.activeElement === firstElement) {
              lastElement.focus();
              e.preventDefault();
            }
          } else {
            if (document.activeElement === lastElement) {
              firstElement.focus();
              e.preventDefault();
            }
          }
        }
      };

      containerRef.current.addEventListener('keydown', handleTabKey);
      
      // التركيز على أول عنصر
      if (firstElement) {
        firstElement.focus();
      }

      return () => {
        if (containerRef.current) {
          containerRef.current.removeEventListener('keydown', handleTabKey);
        }
        
        // استعادة التركيز السابق
        if (restoreFocus && previousActiveElement.current) {
          previousActiveElement.current.focus();
        }
      };
    }
  }, [trapFocus, restoreFocus]);

  return (
    <div ref={containerRef}>
      {children}
    </div>
  );
};

// مكون للإعلانات للقارئات الشاشة
export const ScreenReaderAnnouncer = ({ message, priority = 'polite' }) => {
  useEffect(() => {
    if (message) {
      const announcer = document.getElementById('accessibility-announcements');
      if (announcer) {
        announcer.setAttribute('aria-live', priority);
        announcer.textContent = message;
        
        // مسح الرسالة بعد فترة
        setTimeout(() => {
          announcer.textContent = '';
        }, 1000);
      }
    }
  }, [message, priority]);

  return null;
};

// مكون لتحسين التباين
export const ContrastEnhancer = ({ children, highContrast = false }) => {
  useEffect(() => {
    if (highContrast) {
      document.body.classList.add('high-contrast');
    } else {
      document.body.classList.remove('high-contrast');
    }

    return () => {
      document.body.classList.remove('high-contrast');
    };
  }, [highContrast]);

  return children;
};

// مكون لتحسين حجم النص
export const TextSizeEnhancer = ({ children, textSize = 'normal' }) => {
  useEffect(() => {
    document.body.classList.remove('text-small', 'text-normal', 'text-large', 'text-extra-large');
    document.body.classList.add(`text-${textSize}`);

    return () => {
      document.body.classList.remove('text-small', 'text-normal', 'text-large', 'text-extra-large');
    };
  }, [textSize]);

  return children;
};

// Hook لإدارة إعدادات إمكانية الوصول
export const useAccessibility = () => {
  const [settings, setSettings] = React.useState(() => {
    const saved = localStorage.getItem('accessibility-settings');
    return saved ? JSON.parse(saved) : {
      highContrast: false,
      textSize: 'normal',
      reduceMotion: false,
      screenReader: false
    };
  });

  const updateSettings = (newSettings) => {
    const updated = { ...settings, ...newSettings };
    setSettings(updated);
    localStorage.setItem('accessibility-settings', JSON.stringify(updated));
    
    // تطبيق الإعدادات على الصفحة
    if (updated.reduceMotion) {
      document.body.classList.add('reduce-motion');
    } else {
      document.body.classList.remove('reduce-motion');
    }
  };

  const announce = (message, priority = 'polite') => {
    const announcer = document.getElementById('accessibility-announcements');
    if (announcer) {
      announcer.setAttribute('aria-live', priority);
      announcer.textContent = message;
      setTimeout(() => {
        announcer.textContent = '';
      }, 1000);
    }
  };

  return {
    settings,
    updateSettings,
    announce
  };
};

// أنماط CSS لتحسين إمكانية الوصول
export const AccessibilityStyles = `
  /* تحسين التركيز */
  .keyboard-navigation *:focus {
    outline: 3px solid var(--primary-color) !important;
    outline-offset: 2px !important;
  }

  /* تحسين التباين العالي */
  .high-contrast {
    filter: contrast(150%);
  }

  .high-contrast * {
    border-color: #000 !important;
  }

  /* أحجام النص */
  .text-small { font-size: 14px; }
  .text-normal { font-size: 16px; }
  .text-large { font-size: 18px; }
  .text-extra-large { font-size: 20px; }

  /* تقليل الحركة */
  .reduce-motion * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }

  /* تحسين الروابط */
  a:focus, button:focus {
    text-decoration: underline;
  }

  /* تحسين النماذج */
  input:focus, textarea:focus, select:focus {
    box-shadow: 0 0 0 3px rgba(46, 126, 240, 0.3) !important;
  }

  /* إخفاء المحتوى للقارئات الشاشة فقط */
  .sr-only {
    position: absolute !important;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    margin: -1px !important;
    overflow: hidden !important;
    clip: rect(0, 0, 0, 0) !important;
    white-space: nowrap !important;
    border: 0 !important;
  }
`;

export default AccessibilityEnhancer;
